// 時間前入札の一覧系の表 (`IntradayBidTable` の入札一覧と、 板発注の自社入札表) の
// ステータス列が表示する「合成ステータス」を導出する純関数 (入札履歴タブ・詳細モーダルも、
// 一部約定の札だけ同じ判定を通す)。 約定軸 (bidStatus) + 送信軸 (sendStatus) + 承認軸
// (approvalState) の 3 軸を 1 列に合成する (AC-BIN-035、 独立した「承認」列は持たない):
//
//   約定軸 (bidStatus) が終端 (取消 / 約定 / 失効等) ならそれを優先し、
//   非終端で送信軸が RESERVED なら「予約中」、 JEPX の受け付け前なら「送信中」を表示する。
//   それ以外は承認軸 (差戻し / 承認待ち / 承認済) を見て、 該当しなければ約定軸から
//   「承認待ち」 / 「入札中」 / 「下書き」 に倒す。 「承認済」 は遷移先の予約中 / 送信中で
//   表し (AC-BIN-051)、 「送信失敗」 の区分は無い (送れるまで送り直す札は送信中、 #10680)。
//
// 判定 SoT はあくまで backend の bidStatus / sendStatus / approvalState 値そのもの。
// 例外は一部約定 (PARTIAL_MATCHED) の札で、 残りが
// まだ板にあるあいだは「入札中」を優先して表示する (AC-BIN-035)。 板にあるかは backend が
// 札ごとに返す取下可否 (`withdrawable`)・板取消の進行状態 (`withdrawState`) と価格種別で決める
// ([[isPartialMatchOnBoard]])。
import {
  BiddingApprovalState,
  BiddingBidSendStatus,
  BiddingIntradayOrderStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { BiddingIntradayOrderWithdrawState } from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'

import { isDraftWithSendErrorReason } from '~/models/bidDraftReturnReason'
import { isKnownOnBoardByBidNo } from '~/models/intradayJepxBidNo'
import {
  type IntradayBidRow,
  isMarketOrderRow,
} from '~/pages/IntradayBidding/models/intradayBidRow'

/**
 * 一覧系の表のステータス列が表示しうる合成ステータス (AC-BIN-035 の区分)。
 * `autoCancelled` (自動取消) は板に並んでいた札が約定ゼロのまま自動取消時刻に達して
 * 取り下げられた札 (AC-BIN-017)。 約定軸 AUTO_CANCELLED から写す終端で、 取消ボタンは backend の
 * 取下可否 (`withdrawable`) に従い出さない。
 *
 * `draft` (下書き) / `pendingApproval` (承認待ち) / `rejected` (差戻し) は承認軸で決まる区分
 * (独立した「承認」列は持たない。 AC-BSP-047 と対称)。
 *
 * `unmatched` (未約定) / `expired` (失効) は #7144 論点2 決定A (2026-09-07 PdM 判断、
 * AC-BIN-019/035/036) で追加された区分。 GC 到達時点で JEPX の板に並んだが約定ゼロなら
 * `unmatched`、 一度も並べなかったなら `expired` (監査時の責任所在が異なるため別区分)。
 *
 * `sending` (送信中) は JEPX へ送り始めてから受け付け (入札番号) を確認するまで (AC-BIN-035)。
 * 送信に失敗して送り直している札 (業務ステータス FAILED〈送信失敗〉) も送信中に入れる。
 * 「送信失敗」 の区分は無い (#10680、 Discussion「送信失敗を送信中に改める」 決定 B。 backend の
 * enum は送り直しの状態として FAILED のまま残し、 区分は表示で導く。 スポットの
 * `bidStatusCategory.ts` と同じ)。
 */
export type IntradayTableStatus =
  | 'draft'
  | 'pendingApproval'
  | 'rejected'
  | 'sending'
  | 'waiting'
  | 'reserved'
  | 'partialMatched'
  | 'matched'
  | 'cancelled'
  | 'autoCancelled'
  | 'unmatched'
  | 'expired'

/**
 * bidStatus のうち「終端」(= 送信軸に関わらずその表示が優先される) とみなす値。
 *
 * `composeIntradayTableStatus` の合成優先順位 (bidStatus 終端 > 送信軸・承認軸) の SoT。
 * UNMATCHED (未約定) / EXPIRED (失効) も #7144 論点2 決定Aの終端値
 * (AC-BIN-019/035/036)。それぞれ専用の合成ステータスへ写す (T-52)。
 * FAILED (送信失敗) は終端ではない (送れるまで送り直す) が、 送信軸が送信中・再送待ち・
 * 送信失敗確定のどれでも同じ「送信中」 に写すため、 送信軸を見ずに決める側としてここに入れる
 * (AC-BIN-035)。 GC 前に内容を理由に拒否された札は backend が下書きに戻す (AC-BIN-115、
 * PR #11921)。 それ以外の FAILED (送り直し中・重複応答で板に見つからない札・GC 後に
 * 受け付けを確かめている札) は送信中で出す (「送信失敗」 の区分は無い)。
 */
export const TERMINAL_BID_STATUS: ReadonlySet<BiddingIntradayOrderStatus> =
  new Set([
    BiddingIntradayOrderStatus.PARTIAL_MATCHED,
    BiddingIntradayOrderStatus.ALL_MATCHED,
    BiddingIntradayOrderStatus.CANCELLED,
    BiddingIntradayOrderStatus.AUTO_CANCELLED,
    BiddingIntradayOrderStatus.FAILED,
    BiddingIntradayOrderStatus.UNMATCHED,
    BiddingIntradayOrderStatus.EXPIRED,
  ])

/**
 * 送信軸が送信失敗系 (再送待ち SEND_FAILED / 送信失敗確定 SEND_FAILED_FINAL) か。
 * 送信失敗系でも、 JEPX 入札番号を持つ札は板にある (AC-BIN-115、 #10491) ので呼び出し側が
 * [[isKnownOnBoardByBidNo]] で除く。
 */
const isFailedSendStatus = (
  sendStatus: BiddingBidSendStatus | undefined,
): boolean =>
  sendStatus === BiddingBidSendStatus.SEND_FAILED ||
  sendStatus === BiddingBidSendStatus.SEND_FAILED_FINAL

/**
 * 一部約定した札の残りが、まだ板にあるか (AC-BIN-035、 #8806)。 板にあるあいだは一部約定
 * していてもステータスは「入札中」で、 残りが取り下げ・GC・成行の削除で終わった時に
 * 「部分約定」へ変わる。 約定ゼロの入札中の札は対象外 (もとから入札中)。
 *
 * 板にあるかは backend が状態と GC から返す取下可否 (`withdrawable`) で決める。 AC-BIN-008 は
 * 取消ボタンを最終結果の札には出さないので、 取消を受け付ける一部約定の札は最終結果ではない。
 * 取消処理中 (AC-BIN-008b) の札は、 取消ボタンを出さないため取下不可で返るが、 取消が成立する
 * までは入札中のままなので板にある側に数える。 成行は JEPX が残りを即時に削除する
 * (AC-BIN-004d) ため、 取下可でも板に残らない。
 *
 * 取下可否は取消ボタンの出し分けにも使う値なので、 取下可否と板にあるかが食い違う札を
 * backend が足す時 (取消処理中のほかに入札中のまま取消ボタンを出さない札、 要確認のまま GC を
 * 迎えて入札中のまま終える札 AC-BIN-127 など) は、 この判定も合わせて直す。
 * 要確認 (`needsReview`) の一部約定の札は、 GC 後に取下不可で返っても入札中のまま板に
 * あるものとして数える (取り込み元で状態を確認できていない間は終わった札にしない)。
 */
export const isPartialMatchOnBoard = (
  row: Pick<
    IntradayBidRow,
    | 'bidStatus'
    | 'withdrawable'
    | 'withdrawState'
    | 'orderMethod'
    | 'needsReview'
  >,
): boolean =>
  row.bidStatus === BiddingIntradayOrderStatus.PARTIAL_MATCHED &&
  (row.withdrawable ||
    row.withdrawState === BiddingIntradayOrderWithdrawState.PENDING ||
    row.needsReview === true) &&
  !isMarketOrderRow(row)

/**
 * 合成ステータスを導く札の値。 一覧の行 (`IntradayBidRow`) はそのまま渡し、 詳細モーダルは
 * 詳細の札から組む。 `approvalState` は backend の承認状態のまま渡す
 * ([[composeIntradayTableStatus]])。
 */
export type IntradayStatusSource = Pick<
  IntradayBidRow,
  | 'bidStatus'
  | 'sendStatus'
  | 'withdrawable'
  | 'withdrawState'
  | 'orderMethod'
  | 'needsReview'
> & {
  approvalState: BiddingApprovalState | undefined
  /** JEPX 入札番号。 付いていれば JEPX が受け付けた札 (送信中ではない。 AC-BIN-115)。 */
  jepxBidNo?: string | undefined
}

/**
 * 約定軸 (bidStatus) + 送信軸 (sendStatus) + 承認軸 (approvalState) を 1 列の
 * 合成ステータスに写す。
 *
 * `approvalState` は backend の承認状態 (一覧 RPC の `approval_state`) をそのまま受ける。
 * 承認状態を N/A に畳んだ値は渡さない (承認済が N/A に畳まれ、 下書きと区別できなくなる)。
 *
 * 優先順位:
 *   1. bidStatus が終端 (PARTIAL_MATCHED / ALL_MATCHED / CANCELLED / AUTO_CANCELLED /
 *      FAILED / UNMATCHED / EXPIRED) → その終端をそのまま返す (送信軸・承認軸は見ない)。
 *      UNMATCHED → 'unmatched'、EXPIRED → 'expired' をそれぞれ個別に返す
 *      (AC-BIN-019/035/036、監査時の責任所在が異なるため別区分)。 PARTIAL_MATCHED は
 *      残りが板にあるあいだ ([[isPartialMatchOnBoard]]) は 'waiting' (入札中を優先して
 *      表示する。 AC-BIN-035)、 札が終わったら 'partialMatched' を返す。 FAILED (送信に
 *      失敗して送り直している札。 GC 後は JEPX に届いたかを確かめるまで) は、 JEPX 入札番号が
 *      無ければ 'sending' (送信中。 「送信失敗」 の区分は無い、 #10680)、 あれば 'waiting'
 *      (取り込みが板で見つけた札、 AC-BIN-115)。
 *   2. 非終端で sendStatus が RESERVED → 'reserved' (AC-09、「入札中」と区別)
 *   2a. 非終端で sendStatus が ENQUEUED / SENDING (送信待ち・送信中) か送信失敗系
 *      (SEND_FAILED / SEND_FAILED_FINAL、 #7430 より前に書かれた 入札中 + 送信失敗の行) で
 *      JEPX 入札番号が無い → 'sending' (送信中。 JEPX へ送り始めてから受け付けを確認する
 *      まで、 AC-BIN-035)
 *   3. 非終端・非予約で approvalState が REJECTED → 'rejected' (差戻し、AC-BIN-035)
 *   4. 非終端・非予約で approvalState が PENDING → 'pendingApproval' (承認待ち、
 *      AC-BIN-035。 承認申請 (RequestApproval) は bidStatus を DRAFT のまま approvalState
 *      だけ PENDING にするため、 この分岐が承認申請中の一次判定になる)
 *   5. 非終端・非予約で approvalState が APPROVED → 'sending' (送信中。 承認済は JEPX 送信
 *      進行中の過渡状態で、 遷移先の予約中 / 送信中で表す〈AC-BIN-051〉。 予約中は 2 が先に
 *      決める。 「承認済」 とは出さない)
 *   6. bidStatus が PENDING (承認申請中) → 'pendingApproval'
 *   7. bidStatus が APPROVED (承認済) → 'sending' (5 と同じ理由。 AC-BIN-051)
 *   7a. bidStatus が PLACED → 'waiting' (= 入札中)
 *   8. それ以外 (UNSPECIFIED / DRAFT) → 'draft' (下書き)
 */
export const composeIntradayTableStatus = (
  row: IntradayStatusSource,
): IntradayTableStatus => {
  const { bidStatus, sendStatus, approvalState } = row
  if (TERMINAL_BID_STATUS.has(bidStatus)) {
    switch (bidStatus) {
      case BiddingIntradayOrderStatus.PARTIAL_MATCHED:
        return isPartialMatchOnBoard(row) ? 'waiting' : 'partialMatched'
      case BiddingIntradayOrderStatus.ALL_MATCHED:
        return 'matched'
      case BiddingIntradayOrderStatus.CANCELLED:
        return 'cancelled'
      case BiddingIntradayOrderStatus.AUTO_CANCELLED:
        return 'autoCancelled'
      case BiddingIntradayOrderStatus.FAILED:
        return isKnownOnBoardByBidNo(row) ? 'waiting' : 'sending'
      case BiddingIntradayOrderStatus.UNMATCHED:
        return 'unmatched'
      case BiddingIntradayOrderStatus.EXPIRED:
        return 'expired'
      default:
        return 'draft' // unreachable (TERMINAL_BID_STATUS のみ)
    }
  }

  if (sendStatus === BiddingBidSendStatus.RESERVED) {
    return 'reserved'
  }
  if (
    (sendStatus === BiddingBidSendStatus.ENQUEUED ||
      sendStatus === BiddingBidSendStatus.SENDING ||
      isFailedSendStatus(sendStatus)) &&
    !isKnownOnBoardByBidNo(row)
  ) {
    return 'sending'
  }

  if (approvalState === BiddingApprovalState.REJECTED) {
    return 'rejected'
  }
  if (approvalState === BiddingApprovalState.PENDING) {
    return 'pendingApproval'
  }
  if (approvalState === BiddingApprovalState.APPROVED) {
    return 'sending'
  }

  switch (bidStatus) {
    case BiddingIntradayOrderStatus.PENDING:
      return 'pendingApproval'
    case BiddingIntradayOrderStatus.APPROVED:
      return 'sending'
    case BiddingIntradayOrderStatus.PLACED:
      return 'waiting'
    default:
      return 'draft'
  }
}

/**
 * 一覧系の表のステータス列に何を出すか (#7430)。 表示の優先順位の判断はここに集め、
 * `IntradayBidStatusCell` (入札一覧・入札履歴・板発注の自社入札表が共有) と詳細モーダルは
 * この値で描くだけにする。
 *
 * - `status`: 合成ステータス ([[composeIntradayTableStatus]]) のバッジ
 * - `withSendErrorReason`: true なら送れない原因の分類と理由を hover で添える (AC-BIN-059 /
 *   AC-BIN-035: 送れない理由はステータス区分では分けない)。 送れない原因が分かっている送信中の
 *   札 (送り直している札と、 #7430 より前に書かれた 入札中 + 送信失敗の行) に加え、 送信失敗の
 *   後に失効・取消になった札と、 送る直前の再検証の違反で下書きに戻した札 (送信軸は空) にも
 *   添える (なぜ失効・取消・下書きになったかを後から確かめるため、 AC-BIN-059)。 原因が
 *   未供給なら tooltip 側がバッジだけを出す
 *
 * 送信軸のタグ (再送中 / 送信失敗) は出さない。 JEPX の受け付けを確認するまでは送信中で、
 * 「送信失敗」 の区分は無い (#10680)。 取り込みが板に同じ札を見つけて JEPX 入札番号を紐付け、
 * 再送を止めた札は、 送信軸が失敗系のまま残っても「入札中」 を出す (AC-BIN-115、 #10491)。
 */
export type IntradayStatusCell = {
  status: IntradayTableStatus
  withSendErrorReason: boolean
}

/**
 * 送信軸が送信失敗系の時に、 原因分類の tooltip を添える合成ステータス (AC-BIN-059)。
 * 送り直している送信中 (`sending`) と、 送信失敗の後に終わった失効 (`expired`)・取消
 * (`cancelled`)。 backend は取消・失効の後も送信軸を SEND_FAILED / SEND_FAILED_FINAL の
 * まま返す (`IntradayOrder.Withdraw` / `ExpireNeverReachedPastGc`)。
 */
const STATUSES_WITH_SEND_ERROR_REASON: ReadonlySet<IntradayTableStatus> =
  new Set(['sending', 'expired', 'cancelled'])

export const intradayStatusCellOf = (
  row: IntradayStatusSource & Pick<IntradayBidRow, 'sendErrorCategory'>,
): IntradayStatusCell => {
  const status = composeIntradayTableStatus(row)
  const sendHasFailed =
    isFailedSendStatus(row.sendStatus) ||
    row.bidStatus === BiddingIntradayOrderStatus.FAILED
  return {
    status,
    withSendErrorReason:
      (STATUSES_WITH_SEND_ERROR_REASON.has(status) && sendHasFailed) ||
      (status === 'draft' && isDraftWithSendErrorReason(row)),
  }
}

/**
 * 入札履歴に出さない合成ステータス (AC-BIN-024 / §5 ステータス区分)。 入札履歴は終わった札
 * だけを含むので、 JEPX へ出す前の札 (下書き・承認待ち・差戻し・予約中) と板に並んでいる札
 * (入札中。 要確認のまま GC を迎えた札 AC-BIN-127 を含む) は出さない。 送信中は AC-BIN-024 の
 * 区分にあるので出す。 JEPX に出さないまま GC を迎えた札は失効 (AC-BIN-099) として出す。
 */
const BID_HISTORY_HIDDEN_STATUSES: ReadonlySet<IntradayTableStatus> = new Set([
  'draft',
  'pendingApproval',
  'rejected',
  'reserved',
  'waiting',
])

export const isShownInBidHistory = (row: IntradayStatusSource): boolean =>
  !BID_HISTORY_HIDDEN_STATUSES.has(composeIntradayTableStatus(row))
